import type { CreatePriceSessionResponse } from '@/api';
import BrickBox from '@/components/brick-box';
import { connectColors } from '@/theme/colors';
import { Button, Modal } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { format, formatDistanceToNow } from 'date-fns';
import type { RefObject } from 'react';
import { useMemo } from 'react';
import {
  type ChangeSummary,
  isApproveCreatePriceMode,
} from '../create-price-helpers';
import type { CreatePriceMode } from '../create-price-search-params';

type SessionRecoveryModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onContinue: () => void;
  onDiscard: () => void;
  isDiscarding: boolean;
  session: CreatePriceSessionResponse;
};

const formatPriceUnitsSummary = (
  session: CreatePriceSessionResponse,
): string => {
  const params = session._queryParameters;
  if (!params.priceUnitIds || params.priceUnitIds.length === 0) return '';
  return params.priceUnitIds.join(', ');
};

const getSessionChangeSummary = (
  session: CreatePriceSessionResponse,
  isApproveMode: boolean,
): ChangeSummary[] => {
  const rows = session.modifiedRows ?? [];
  if (rows.length === 0) return [];

  if (isApproveMode) {
    const approvedRows = rows.filter((r) => r.priceStatus === 'Approved');
    if (approvedRows.length === 0) return [];
    const skuCount = new Set(approvedRows.map((r) => r.productId)).size;
    return [
      {
        label: `${skuCount} ${skuCount === 1 ? 'SKU' : 'SKUs'} with price approvals`,
        count: skuCount,
      },
    ];
  }

  const uniqueProducts = new Set(rows.map((r) => r.productId));
  const skuCount = uniqueProducts.size;
  return [
    {
      label: `${skuCount} ${skuCount === 1 ? 'SKU' : 'SKUs'} with price changes`,
      count: skuCount,
    },
  ];
};

export const SessionRecoveryModal = ({
  isOpen,
  triggerRef,
  onClose,
  onContinue,
  onDiscard,
  isDiscarding,
  session,
}: SessionRecoveryModalProps) => {
  const mode = session.mode as CreatePriceMode;
  const isApproveMode = isApproveCreatePriceMode(mode);

  const validFromDate = useMemo(() => {
    const date = new Date(session._queryParameters.validFromDate);
    if (Number.isNaN(date.getTime())) return '';
    return format(date, 'dd.MM.yyyy');
  }, [session._queryParameters.validFromDate]);

  const priceUnitsSummary = useMemo(
    () => formatPriceUnitsSummary(session),
    [session],
  );

  const changeSummary = useMemo(
    () => getSessionChangeSummary(session, isApproveMode),
    [session, isApproveMode],
  );

  const relativeTime = session.createdAt
    ? formatDistanceToNow(session.createdAt, { addSuffix: true })
    : '';

  const modalTitle = isApproveMode
    ? 'Continue approval session?'
    : 'Continue pricing session?';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      size="medium"
    >
      <Modal.Title>{modalTitle}</Modal.Title>
      <Modal.Content>
        <Flex direction="column" gap="16px">
          <Text color={connectColors.core.offBlack}>
            You have an unpublished {isApproveMode ? 'approval' : 'pricing'}{' '}
            session with unsaved changes.
          </Text>

          <BrickBox color={connectColors.core.blue[50]}>
            <Flex direction="column" gap="8px" padding="16px">
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontWeight="medium" color={connectColors.core.offBlack}>
                  {isApproveMode ? 'Approve prices' : 'Create prices'}
                </Text>
                {relativeTime && (
                  <Text
                    className="body-xs-regular"
                    color={connectColors.core.slate[600]}
                  >
                    Started {relativeTime}
                  </Text>
                )}
              </Flex>

              <Flex direction="column" gap="4px">
                <Text color={connectColors.core.offBlack}>
                  <Text as="span" fontWeight="medium">
                    Valid from:
                  </Text>{' '}
                  {validFromDate}
                </Text>

                {priceUnitsSummary && (
                  <Text color={connectColors.core.offBlack}>
                    <Text as="span" fontWeight="medium">
                      Price units:
                    </Text>{' '}
                    {priceUnitsSummary}
                  </Text>
                )}

                {changeSummary.map((change) => (
                  <Text key={change.label} color={connectColors.core.offBlack}>
                    <Text as="span" fontWeight="bold">
                      {change.label}
                    </Text>
                  </Text>
                ))}
              </Flex>
            </Flex>
          </BrickBox>
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Button
          appearance="ghost"
          variant="primary"
          size="medium"
          onClick={onDiscard}
          disabled={isDiscarding}
        >
          Discard and start new
        </Button>
        <Button
          appearance="solid"
          variant="primary"
          size="medium"
          onClick={onContinue}
          disabled={isDiscarding}
        >
          Continue session
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
